import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateTraningVideoMutation,
  useGetTraningVideoQuery,
  useUpdateTraningVideoMutation
} from '../../../../../services/endpoints/content/trainingVideo';
import { getCurrentUser } from '../../../../../services/helper';
import { FadeIn } from '../../../../animations/FadeIn';
import { TrainingVideosForm } from './TrainingVideosForm';
import { userCanEditCreateArchive_TrainingVideos } from './TrainingVideosView';

const steps = [{ name: 'Details', form: TrainingVideosForm }];

export interface FormValues {
  id?: number;
  name: string;
  code: string;
  category: string;
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;
export type CreateFormValues = Overwrite<FormValues, {}>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

const trainingVideosLinksMap = (id?: number | string) => {
  return {
    edit: `/admin/training/videos/${id}/edit`,
    detail: `/admin/training/videos/${id}`,
    list: `/admin/training/videos`
  };
};

interface AddEditTrainingVideosProps {
  editing?: boolean;
}

export const AddEditTrainingVideos: React.FC<AddEditTrainingVideosProps> = ({ editing }) => {
  const currentUser = getCurrentUser();
  const params = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createTrainingVideos] = useCreateTraningVideoMutation();
  const [updateTrainingVideos] = useUpdateTraningVideoMutation();

  const {
    data: trainingVideosData,
    error: trainingVideosError,
    isLoading: trainingVideosLoading
  } = useGetTraningVideoQuery(Number(params.id!), { skip: !editing || !params.id });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    code: '',
    category: ''
  });

  const validationSchemas = [
    Yup.object({
      name: Yup.string().required('Name is required'),
      code: Yup.string().required('Description is required')
    })
  ];

  useEffect(() => {
    if (editing && trainingVideosError && !trainingVideosLoading) {
      addToast('Error while loading admin', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, trainingVideosError, trainingVideosLoading]);

  useEffect(() => {
    let values;
    if (trainingVideosData && trainingVideosData?.trainingVideo) {
      values = trainingVideosData.trainingVideo;
    }

    if (values?.name)
      setInitialValues({
        id: values.id,
        name: values.name,
        code: values?.code ?? '',
        category: values?.category
      });

    if (!userCanEditCreateArchive_TrainingVideos(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/training/videos`);
    }
  }, [trainingVideosData]);

  const formatFormValue = (formValue?: FormValues): CreateFormValues => {
    return JSON.parse(JSON.stringify(formValue)) as CreateFormValues;
  };

  const handleAddTrainingVideos = async (formValue: FormValues) => {
    try {
      let submission = formatFormValue(formValue);

      if (editing) {
        const res = await updateTrainingVideos({
          ...submission,
          id: Number(params.id)!
        }).unwrap();
        if (res && res.trainingVideo) {
          navigate(trainingVideosLinksMap(res.trainingVideo.id!)?.list);
          addToast(`Guide updated succesfully`, 'success');
        }
      } else {
        const res = await createTrainingVideos(submission).unwrap();
        if (res && res.trainingVideo) {
          navigate(trainingVideosLinksMap(res.trainingVideo.id!)?.list);
          addToast('Guide created succesfully', 'success');
        } else throw new Error('A problem happened while creating Guide');
      }
    } catch (e) {
      if (e.data && e.data.error) {
        addToast(e.data.error[0], 'error');
      }
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        id: true,
        name: true,
        code: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text="Back"
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Guide
          </h2>
          {steps.length > 1 && (
            <Stepper
              editing
              steps={steps}
              currentStep={currentStep}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddTrainingVideos}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
